import type { AiToolCallDetail } from "@repo/shared/types";
import { ChevronDown, Copy, Loader2, Wrench } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./tool-calls-block.i18n";

interface ToolCallsBlockProps {
  tools: AiToolCallDetail[];
  defaultOpen?: boolean;
}

function CopyBtn({ text, label }: { text: string; label: string }) {
  const t = useT(T);
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Copy className="w-3 h-3" />
      {copied ? t.copied : label}
    </button>
  );
}

export function ToolCallsBlock({ tools, defaultOpen = false }: ToolCallsBlockProps) {
  const t = useT(T);
  const [open, setOpen] = useState(defaultOpen);
  const [selectedTool, setSelectedTool] = useState<AiToolCallDetail | null>(null);
  const runningCount = tools.filter((tool) => tool.status === "running").length;

  const inputText = selectedTool?.args ? JSON.stringify(selectedTool.args, null, 2) : "";
  const outputText = selectedTool?.result ? formatResult(selectedTool.result) : "";

  return (
    <>
      <div className="mt-1 text-[11px] min-w-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          {runningCount > 0 ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Wrench className="w-3 h-3" />
          )}
          <span>
            {t.tools(tools.length)}
            {runningCount > 0 && t.running(runningCount)}
          </span>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="mt-1 space-y-1">
            {tools.map((tool) => (
              <button
                type="button"
                key={tool.toolCallId}
                className="w-full min-w-0 text-left pl-3 border-l-2 border-muted-foreground/30 text-muted-foreground hover:text-foreground transition-colors cursor-pointer overflow-hidden"
                onClick={() => tool.status !== "running" && setSelectedTool(tool)}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {tool.status === "running" ? (
                    <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                  ) : tool.success ? (
                    <span className="text-success flex-shrink-0">{"\u2713"}</span>
                  ) : (
                    <span className="text-destructive flex-shrink-0">{"\u2717"}</span>
                  )}
                  <span className="font-medium truncate">{tool.name}</span>
                  {tool.durationMs != null && (
                    <span className="text-[10px] text-muted-foreground/60">
                      {tool.durationMs}ms
                    </span>
                  )}
                </div>
                {tool.status === "error" && tool.result && (
                  <div className="mt-0.5 text-[10px] text-destructive/70 truncate">
                    {tool.result.slice(0, 100)}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={selectedTool !== null} onOpenChange={(v) => !v && setSelectedTool(null)}>
        <DialogContent className="w-[50vw] max-w-none max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Wrench className="w-4 h-4" />
              {selectedTool?.name}
              {selectedTool?.durationMs != null && (
                <span className="text-xs text-muted-foreground font-normal">
                  {selectedTool.durationMs}ms
                </span>
              )}
              {selectedTool?.success != null && (
                <span className={selectedTool.success ? "text-success" : "text-destructive"}>
                  {selectedTool.success ? "\u2713" : "\u2717"}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">{t.details}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-4 text-xs font-mono">
            {inputText && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-medium">
                    {t.input}
                  </span>
                  <CopyBtn text={inputText} label={t.copy} />
                </div>
                <pre className="whitespace-pre-wrap break-all leading-relaxed bg-muted rounded-md p-3">
                  {inputText}
                </pre>
              </div>
            )}
            {outputText && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-medium">
                    {t.output}
                  </span>
                  <CopyBtn text={outputText} label={t.copy} />
                </div>
                <pre className="whitespace-pre-wrap break-all leading-relaxed bg-muted rounded-md p-3">
                  {outputText}
                </pre>
              </div>
            )}
          </div>
          {inputText && outputText && (
            <div className="flex justify-end pt-2 border-t">
              <CopyBtn text={`INPUT:\n${inputText}\n\nOUTPUT:\n${outputText}`} label={t.copyAll} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatResult(result: string): string {
  try {
    return JSON.stringify(JSON.parse(result), null, 2);
  } catch {
    return result;
  }
}
